import {
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
  HttpErrorResponse,
  HttpInterceptorFn,
} from '@angular/common/http';
import { inject, isDevMode } from '@angular/core';
import { Observable, throwError, from, EMPTY } from 'rxjs';
import { catchError, switchMap, timeout } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';
import { loginRequest } from 'src/configurations/b2c/b2c.config';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { Router } from '@angular/router';
import { RoutePaths } from '../app.routes';
import { ToastrService } from 'ngx-toastr';
import { StaleDataDialogService } from '../services/stale-data-dialog.service';
import { STALE_DATA_ERROR_TYPE } from '../models/constants';

/** HTTP request timeout in milliseconds (2 minutes) */
const HTTP_TIMEOUT_MS = 120000;

/** Maximum number of token acquisition retry attempts */
const MAX_TOKEN_RETRIES = 3;

/** Base delay for exponential backoff in milliseconds */
const RETRY_BASE_DELAY_MS = 1000;

/** Auth error code indicating session was invalidated due to permission changes */
const AUTH_SESSION_INVALIDATED = 'AUTH_011';

/**
 * Result interface for API responses
 */
export interface Result<T> {
  isSuccess: boolean;
  error?: string;
  value?: T;
}

/**
 * Error response interface
 */
export interface ErrorResponse {
  code: string;
  description: string;
  errorType: number;
}

/**
 * Error response detail interface
 */
export interface ErrorResponseDetail {
  detail: string;
  error: ErrorResponse;
  status: number;
  title: string;
  type: string;
}

/**
 * Checks if the browser is currently online
 * @returns true if online, false if offline
 */
function isOnline(): boolean {
  return navigator.onLine;
}

/**
 * Interceptor to handle errors in the application
 * @param req The original HTTP request
 * @param next The next HTTP handler
 * @returns An observable that emits the HTTP event
 */
export const HttpHandlerInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
  const msalService = inject(MsalService);
  const router = inject(Router);
  const toastr = inject(ToastrService);
  const staleDataDialog = inject(StaleDataDialogService);

  return from(getAccessToken(req, msalService)).pipe(
    switchMap((authReq) =>
      next(authReq).pipe(
        // Add timeout to all HTTP requests
        timeout(HTTP_TIMEOUT_MS),
        catchError((error: any) => {
          // Handle timeout errors
          if (error.name === 'TimeoutError') {
            console.error('[HTTP] Request timeout:', req.url);
            toastr.error(
              'The request took too long to complete. Please try again.',
              'Request Timeout'
            );
            return throwError(() => new Error('Request timeout'));
          }

          if (error instanceof HttpErrorResponse) {
            // Handle custom error code 999
            if (error.status === 999) {
              router.navigate([RoutePaths.Error.Path], {
                queryParams: {
                  reason: 'pharmacy_associate_user_error',
                  code: error.status,
                },
              });
              // Return an empty observable to prevent further error handling
              return EMPTY;
            }

            // Stale-data conflict for plain JSON (non-blob) responses: another user already moved this
            // order or group shipment on, so the screen the caller acted from is out of date. The backend
            // signals this as a 409 whose problem-type ends in the stale-data slug. (Most API calls go
            // through the generated client with responseType 'blob', so the body arrives as a Blob and is
            // handled in the blob branch below — this covers any request that returns parsed JSON instead.)
            if (error.status === 409 && isStaleDataProblem(error.error)) {
              staleDataDialog.showAndReload();
              return EMPTY;
            }

            // Handle Unauthorized (401) - Check network status first
            if (error.status === 401) {
              // Check if we're offline - don't logout if it's a network issue
              if (!isOnline()) {
                console.warn('[HTTP] Network offline, not logging out');
                toastr.warning(
                  'You appear to be offline. Please check your internet connection.',
                  'Network Offline'
                );
                return throwError(() => new Error('Network offline'));
              }

              // Check if this is a session invalidation error (permissions changed)
              const errorCode = error.error?.errorCode;
              if (errorCode === AUTH_SESSION_INVALIDATED) {
                console.warn('[HTTP] Session invalidated (AUTH_011), forcing re-login');
                toastr.warning(
                  'Your permissions have changed. Please sign in again.',
                  'Session Expired'
                );
              }

              const account = msalService.instance.getActiveAccount();
              if (account) {
                console.warn('[HTTP] Unauthorized (401), redirecting to error page');
                // Don't force a logout here — send the user to the error
                // page, where they can choose to log out themselves.
                router.navigate([RoutePaths.Error.Path], {
                  queryParams: {
                    reason: 'unauthorized',
                    code: 401,
                  },
                });
              }
              return throwError(() => new Error('Unauthorized'));
            }

            // Handle Forbidden (403)
            if (error.status === 403) {
              console.warn('[HTTP] Forbidden (403):', req.url);
              toastr.error(
                'You do not have permission to perform this action.',
                'Access Denied'
              );
              return throwError(() => new Error('Forbidden'));
            }

            // Handle blob error responses. The generated API client requests responseType 'blob', so
            // backend error bodies — including our stale-data ProblemDetails — arrive here as a Blob.
            if (error.error instanceof Blob) {
              return from(error.error.text()).pipe(
                switchMap((text) => {
                  let parsed: unknown = null;
                  try {
                    parsed = JSON.parse(text);
                  } catch {
                    // not JSON — fall through to the generic blob handling below
                  }

                  // Stale-data conflict: another user already moved this order/group on, so the screen
                  // is out of date. Show one must-acknowledge dialog and refresh on OK.
                  if (error.status === 409 && isStaleDataProblem(parsed)) {
                    staleDataDialog.showAndReload();
                    return EMPTY;
                  }

                  return handleBlobError(error).pipe(
                    switchMap((errorMessage) => {
                      console.error('[HTTP] Backend error (from blob):', errorMessage);
                      const errorString =
                        typeof errorMessage === 'string'
                          ? errorMessage
                          : JSON.stringify(errorMessage);
                      return throwError(() => new Error(errorString));
                    })
                  );
                })
              );
            }

            // Handle network errors (status 0)
            if (error.status === 0) {
              if (!isOnline()) {
                console.warn('[HTTP] Network offline');
                toastr.warning(
                  'You appear to be offline. Please check your internet connection.',
                  'Network Offline'
                );
              } else {
                console.error('[HTTP] Network error:', error.message);
                toastr.error(
                  'Unable to connect to the server. Please try again.',
                  'Connection Error'
                );
              }
              return throwError(() => new Error('Network error'));
            }

            // Fallback for unexpected errors
            const fallbackMessage =
              error.message || 'An unexpected error occurred';
            console.error('[HTTP] Unexpected error:', fallbackMessage);
            toastr.error(fallbackMessage, 'Error');
            return throwError(() => new Error(fallbackMessage));
          }

          // Pass through if not an HTTP error
          return throwError(() => error);
        })
      )
    )
  );
};

/**
 * Delays execution for a specified time
 * @param ms Milliseconds to delay
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Gets an access token with retry logic and exponential backoff
 * @param req The original HTTP request
 * @param msalService The MSAL service
 * @returns A promise that resolves to the request with auth header
 */
async function getAccessToken(
  req: HttpRequest<any>,
  msalService: MsalService
): Promise<HttpRequest<any>> {
  const instance = msalService.instance;
  let account = instance.getActiveAccount();
  const allAccounts = instance.getAllAccounts();

  if (!account && allAccounts.length > 0) {
    account = allAccounts[0];
    instance.setActiveAccount(account);
    if (isDevMode()) console.log('[Token] Set active account from available accounts');
  }

  // No account — user probably not logged in
  if (!account) {
    console.warn('[Token] No active account found, proceeding without token');
    return req;
  }

  // Request token with API scope
  const tokenRequest = {
    scopes: [...loginRequest.scopes],
    account: account,
  };

  // Retry with exponential backoff
  for (let attempt = 1; attempt <= MAX_TOKEN_RETRIES; attempt++) {
    try {

      const response = await instance.acquireTokenSilent(tokenRequest);

      if (response && response.accessToken) {
        return req.clone({
          setHeaders: {
            Authorization: `Bearer ${response.accessToken}`,
          },
        });
      }
    } catch (error) {
      const isLastAttempt = attempt === MAX_TOKEN_RETRIES;

      // Handle interaction required error - redirect to login
      if (error instanceof InteractionRequiredAuthError) {
        console.warn('[Token] Interaction required, redirecting to login');
        msalService.loginRedirect();
        return req; // Return original request, redirect will handle the rest
      }

      // Log the error
      console.warn(`[Token] Acquisition failed (attempt ${attempt}/${MAX_TOKEN_RETRIES}):`, error);

      // If not last attempt, wait with exponential backoff
      if (!isLastAttempt) {
        const delayMs = RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1);
        if (isDevMode()) console.log(`[Token] Retrying in ${delayMs}ms...`);
        await delay(delayMs);
      } else {
        console.error('[Token] All retry attempts exhausted');
      }
    }
  }

  // Return original request without token if all retries failed
  console.warn('[Token] Proceeding without token after all retries failed');
  return req;
}

/**
 * Returns true when a parsed error body is our stale-data ProblemDetails — its `type` URI ends in the
 * stale-data slug. Safe to call with anything: a Blob, string, or null returns false.
 */
function isStaleDataProblem(body: unknown): boolean {
  const type = (body as { type?: unknown } | null)?.type;
  return typeof type === 'string' && type.includes(STALE_DATA_ERROR_TYPE);
}

/**
 * Handles blob error responses by reading the blob content
 * @param error The HTTP error response containing a blob
 * @returns An observable with the error message extracted from the blob
 */
function handleBlobError(
  error: HttpErrorResponse
): Observable<string | ErrorResponseDetail | Result<any>> {
  return from(
    new Promise<string | ErrorResponseDetail | Result<any>>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          // Try to parse the blob content as JSON
          const errorObj = JSON.parse(reader.result as string);
          if (errorObj && typeof errorObj === 'object') {
            // Check if the error response follows the Result pattern
            if ('isSuccess' in errorObj) {
              const result: Result<any> = errorObj;

              // Handle Conflict (HTTP 409)
              if (error.status === 409) {
                console.warn('Conflict detected:', result.error);
                resolve(result || 'Conflict detected');
                return;
              }

              // Handle General Backend Error
              if (!result.isSuccess && result.error) {
                console.error('Backend error:', result.error);
                resolve(result.error);
                return;
              }
            } else if ('error' in errorObj) {
              // If it has an error property but doesn't follow the full Result pattern
              const errorDetail = errorObj as ErrorResponseDetail;
              resolve(errorDetail);
              return;
            } else {
              // Try to find any error message in the object
              const errorMessage = findErrorMessage(errorObj);
              if (errorMessage) {
                resolve(errorMessage);
                return;
              }
            }
          }

          // Fallback for unexpected formats
          resolve(
            (reader.result as string) ||
              `Error ${error.status}: ${error.statusText}`
          );
        } catch (e) {
          // If it's not JSON, return the text content or a fallback
          const fallbackMessage =
            (reader.result as string) ||
            `Error ${error.status}: ${error.statusText}`;
          console.error('Error parsing blob content:', e);
          resolve(fallbackMessage);
        }
      };
      reader.onerror = () => {
        const fallbackMessage =
          error.message || `Error ${error.status}: ${error.statusText}`;
        console.error('Error reading blob:', fallbackMessage);
        resolve(fallbackMessage);
      };
      reader.readAsText(error.error);
    })
  );
}

/**
 * Recursively searches an object for an error message
 * @param obj The object to search
 * @returns The first error message found or undefined
 */
function findErrorMessage(obj: any): string | undefined {
  if (!obj || typeof obj !== 'object') return undefined;

  // Check common error message properties
  const errorProps = ['message', 'error', 'description', 'detail'];
  for (const prop of errorProps) {
    if (obj[prop] && typeof obj[prop] === 'string') {
      return obj[prop];
    }
  }

  // Recursively search nested objects
  for (const key in obj) {
    if (obj.hasOwnProperty(key) && typeof obj[key] === 'object') {
      const message = findErrorMessage(obj[key]);
      if (message) return message;
    }
  }

  return undefined;
}
